import {
  IonButton,
  IonContent,
  IonDatetime,
  IonDatetimeButton,
  IonFooter,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTitle,
  useIonToast,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthenticationContext } from "../../../providers/AuthenticationProvider/AuthenticationProvider";
import useClubsByCoach from "../../../hooks/useClubsByCoach";
import useFieldsByClub from "../../../hooks/useFieldsByClub";
import useAthletesByCoach from "../../../hooks/useAthletesByCoach";
import useSaveOrUpdateGroup from "../../../hooks/groups/useSaveOrUpdateGroup";
import useUserGroupsByIdAndManager from "../../../hooks/groups/useUserGroupsByIdAndManager";
import useTagsByCoach from "../../../hooks/useTagsByCoach";
import Spinner from "../../../components/Spinner/Spinner";
import { useHistory } from "react-router";
import TagModal from "../../../components/TagModal/TagModal";
import AthletesModal from "../../../components/AthletesModal/AthletesModal";
import TagList from "../../../components/TagList/TagList";
import ParticipantList from "../../../components/ParticipantList/ParticipantList";
import PageTitle from "../../../components/PageTitle/PageTitle";
import SubscriptionList from "../../../components/SubscriptionList/SubscriptionList";
import SubscriptionsModal from "../../../components/SubscriptionsModal/SubscriptionsModal";
import useAPI from "../../../hooks/basic/useAPI";
import { toLocalISO } from "../../../utils/datetime";

const AddOrUpdateGroup = ({ match }) => {
  const { appUserId } = useContext(AuthenticationContext);
  const [subscriptions, setSubscriptions] = useState([]);
  const parsedGroupId =
    match.params.groupId !== "add" ? match.params.groupId : undefined;
  const [groupId, setGroupId] = useState(parsedGroupId);
  const [description, setDescription] = useState("");
  const [label, setLabel] = useState("");
  const [timeStart, setTimeStart] = useState(toLocalISO(new Date()));
  const [timeStop, setTimeStop] = useState(toLocalISO(new Date()));
  const [fromDate, setFromDate] = useState(toLocalISO(new Date()));
  const [toDate, setToDate] = useState(toLocalISO(new Date()));
  const [clubId, setClubId] = useState(null);
  const [fieldId, setFieldId] = useState(null);
  const [dayOfWeek, setDayOfWeek] = useState(null); // [0, 1, 2, 3, 4, 5, 6
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [maxParticipants, setMaxParticipants] = useState(4);
  const saveOrUpdateGroup = useSaveOrUpdateGroup();
  const [present] = useIonToast();
  const history = useHistory();
  const formRef = useRef(null);
  const { callAPI } = useAPI();

  const loadSubscriptions = async () => {
    try {
      const response = await callAPI({
        url: `/subscriptions`,
        params: {
          user_id: appUserId,
        },
      });
      setSubscriptions(response);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadSubscriptions();
  }, []);

  console.log("subscriptionData", subscriptions);
  const [openTagModal, setOpenTagModal] = useState(false);
  const [openSubscriptionsModal, setOpenSubscriptionsModal] = useState(false);
  const [openAthletesModal, setOpenAthletesModal] = useState(false);

  useIonViewWillLeave(() => {
    if (openTagModal || openAthletesModal) {
      setOpenTagModal(false);
      setOpenAthletesModal(false);
      return false;
    }
  });

  const isUpdatePage = parsedGroupId !== undefined;

  const submitForm = () => {
    const submitEvent = new Event("submit", {
      bubbles: true,
      cancelable: true,
    });
    formRef.current.dispatchEvent(submitEvent); // Invia il form programmaticamente senza ricaricare la pagina
  };

  const resetFields = () => {
    setGroupId(null);
    setDescription("");
    setLabel("");
    setTimeStart(toLocalISO(new Date()));
    setTimeStop(toLocalISO(new Date()));
    setFromDate(toLocalISO(new Date()));
    setToDate(toLocalISO(new Date()));
    setClubId(null);
    setFieldId(null);
    setDayOfWeek(null);
    setSelectedAthletes([]);
    setSelectedTags([]);
    setSelectedSubscriptions([]);
    setMaxParticipants(4);
  };

  useIonViewWillEnter(() => {
    resetFields();
  });

  const { data, isLoading: isLoadingClubs } = useClubsByCoach(appUserId);
  const { data: fields, isLoading: isLoadingFields } = useFieldsByClub(clubId);
  const { data: athletes, isLoading: isLoadingAthletes } =
    useAthletesByCoach(appUserId);
  const { data: group, isLoading: isLoadingGroup } =
    useUserGroupsByIdAndManager(
      groupId,
      appUserId,
      !groupId || groupId === "add",
    );

  // A deleted (or non-existent) group must not be editable via its URL: once the
  // fetch settles to null, bounce back to the list. Guarded by isUpdatePage so
  // the "create new" flow (which never loads a group) is untouched.
  useEffect(() => {
    if (isUpdatePage && !isLoadingGroup && group === null) {
      present({
        message: "Gruppo non più disponibile",
        duration: 3000,
        color: "warning",
        position: "bottom",
      });
      history.replace("/admin/groups");
    }
  }, [isUpdatePage, isLoadingGroup, group]);
  console.log("group", groupId);
  const { data: tags, isLoading: isLoadingTags } = useTagsByCoach(appUserId);

  const isLoading =
    isLoadingClubs ||
    isLoadingFields ||
    isLoadingAthletes ||
    isLoadingGroup ||
    isLoadingTags;
  useEffect(() => {
    if (group && group.id) {
      setGroupId(group.id);
      setDescription(group.description);
      setLabel(group.label);
      // Wall-clock round-trip: show the stored Timestamp's local time as-is, with
      // no timezone shift. The old load-only shift (without a matching one on
      // save) made the lesson time drift by the UTC offset on every edit.
      setTimeStart(toLocalISO(new Date(group.fromTime.seconds * 1000)));
      setTimeStop(toLocalISO(new Date(group.toTime.seconds * 1000)));
      setFromDate(toLocalISO(new Date(group.fromDate.seconds * 1000)));
      setToDate(toLocalISO(new Date(group.toDate.seconds * 1000)));
      setClubId(group.club.split("/").pop());
      setFieldId(group.field.split("/").pop());
      setDayOfWeek(+group.dayOfWeek);
      setSelectedAthletes(group.participants);
      setSelectedTags(group.tags || []);
      setSelectedSubscriptions(group.subscriptions || []);
      setMaxParticipants(group.maxParticipants);
    }
  }, [group]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const description = formData.get("description");
    // timeStart/timeStop are local ISO strings (no Z), so new Date() parses them
    // as local wall-clock and the stored Timestamp keeps the intended time.
    const dateTimeStart = new Date(timeStart);
    const dateTimeStop = new Date(timeStop);

    const selectedClubName = data.find((club) => club.id === clubId).nome;
    const selectedFieldName = fields.find((field) => field.id === fieldId).nome;

    try {
      await saveOrUpdateGroup({
        id: groupId ? groupId : null,
        club: `/societa/${clubId}`,
        field: `societa/${clubId}/campo/${fieldId}`,
        dayOfWeek: dayOfWeek.toString(),
        description,
        label,
        managerId: appUserId,
        fromTime: dateTimeStart,
        toTime: dateTimeStop,
        fromDate: new Date(fromDate),
        toDate: new Date(toDate),
        participants: selectedAthletes,
        maxParticipants: +maxParticipants,
        subscriptions: selectedSubscriptions,
        tags: selectedTags,
        clubName: selectedClubName,
        fieldName: selectedFieldName,
      });
      history.goBack();
      present({
        message: "Gruppo salvato con successo",
        duration: 3000,
        color: "primary",
        position: "bottom",
      });
    } catch (error) {
      console.error(error);
      present({
        message: "Errore nel salvataggio del gruppo",
        duration: 3000,
        color: "danger",
        position: "bottom",
      });
    }
  };
  return (
    <IonPage>
      {isLoading && <Spinner />}
      <IonContent>
        <IonTitle
          style={{
            color: "#062a40",
          }}
        >
          <PageTitle>
            {!isUpdatePage && "Crea nuovo gruppo"}
            {isUpdatePage && "Modifica gruppo"}
          </PageTitle>
        </IonTitle>
        {!isLoading && (
          <>
            <form className="TweenerApp_form" onSubmit={onSubmit} ref={formRef}>
              <IonList>
                <IonItem>
                  <IonInput
                    className="ion-text-right"
                    label="Descrizione"
                    name="description"
                    value={description}
                    placeholder="Descrizione"
                    onIonChange={(e) => setDescription(e.target.value)}
                  ></IonInput>
                </IonItem>
                <IonItem>
                  <IonInput
                    className="ion-text-right"
                    label="Etichetta"
                    name="label"
                    value={label}
                    onIonChange={(e) => setLabel(e.target.value)}
                  ></IonInput>
                </IonItem>
                <IonItem>
                  <IonLabel className="custom-label">
                    Giorno della settimana
                  </IonLabel>
                  <IonSelect
                    aria-label="Fruit"
                    interface="action-sheet"
                    placeholder="Giorno"
                    label=""
                    onIonChange={(e) => {
                      setDayOfWeek(e.detail.value);
                    }}
                    value={dayOfWeek}
                  >
                    {data &&
                      [
                        "Domenica",
                        "Lunedì",
                        "Martedì",
                        "Mercoledì",
                        "Giovedì",
                        "Venerdì",
                        "Sabato",
                      ].map((day, id) => (
                        <IonSelectOption key={id} value={id}>
                          {day}
                        </IonSelectOption>
                      ))}
                  </IonSelect>
                </IonItem>
                <IonItem>
                  <IonLabel>Data Inizio</IonLabel>
                  <IonDatetimeButton datetime="fromDate"></IonDatetimeButton>
                  <IonModal keepContentsMounted={true}>
                    <IonDatetime
                      presentation="date"
                      id="fromDate"
                      value={fromDate}
                      onIonChange={(e) => setFromDate(e.detail.value)}
                    ></IonDatetime>
                  </IonModal>
                </IonItem>
                <IonItem>
                  <IonLabel>Data Fine</IonLabel>
                  <IonDatetimeButton datetime="toDate"></IonDatetimeButton>
                  <IonModal keepContentsMounted={true}>
                    <IonDatetime
                      presentation="date"
                      id="toDate"
                      value={toDate}
                      onIonChange={(e) => setToDate(e.detail.value)}
                    ></IonDatetime>
                  </IonModal>
                </IonItem>
                <IonItem>
                  <IonLabel>Ora inizio</IonLabel>
                  <IonDatetimeButton datetime="timeStart"></IonDatetimeButton>

                  <IonModal keepContentsMounted={true}>
                    <IonDatetime
                      presentation="time"
                      id="timeStart"
                      value={timeStart}
                      onIonChange={(e) => setTimeStart(e.detail.value)}
                    // timeZone="local"
                    ></IonDatetime>
                  </IonModal>
                </IonItem>

                <IonItem>
                  <IonLabel id="eee">Ora fine</IonLabel>
                  <IonDatetimeButton
                    aria-labelledby="eee"
                    datetime="timeEnd"
                  ></IonDatetimeButton>

                  <IonModal keepContentsMounted={true}>
                    <IonDatetime
                      // timeZone="local"
                      presentation="time"
                      id="timeEnd"
                      value={timeStop}
                      onIonChange={(e) => setTimeStop(e.detail.value)}
                    ></IonDatetime>
                  </IonModal>
                </IonItem>
                <IonItem>
                  <IonLabel className="custom-label">
                    Seleziona un club
                  </IonLabel>
                  <IonSelect
                    aria-label="Fruit"
                    interface="action-sheet"
                    placeholder="Seleziona un club"
                    label=""
                    onIonChange={(e) => {
                      setClubId(e.detail.value);
                      setFieldId(null);
                    }}
                    value={clubId}
                  >
                    {data &&
                      data.map((club) => (
                        <IonSelectOption key={club.id} value={club.id}>
                          {club.nome}
                        </IonSelectOption>
                      ))}
                  </IonSelect>
                </IonItem>
                <IonItem>
                  <IonLabel className="custom-label">
                    Seleziona un campo
                  </IonLabel>
                  <IonSelect
                    aria-label="Seleziona un campo"
                    label=""
                    interface="action-sheet"
                    placeholder="Seleziona un campo"
                    value={fieldId}
                    onIonChange={(e) => setFieldId(e.detail.value)}
                  >
                    {fields &&
                      fields.map((field) => (
                        <IonSelectOption key={field.id} value={field.id}>
                          {field.nome}
                        </IonSelectOption>
                      ))}
                  </IonSelect>
                </IonItem>
                <IonItem>
                  <IonLabel>Massimo partecipanti</IonLabel>
                  <IonInput
                    min={1}
                    type="number"
                    className="ion-text-right"
                    value={maxParticipants}
                    onIonChange={(e) => setMaxParticipants(e.target.value)}
                  ></IonInput>
                </IonItem>
                <IonItem onClick={() => setOpenSubscriptionsModal(true)}>
                  <IonLabel className="custom-label">Tariffe</IonLabel>
                  <SubscriptionList
                    subscriptions={selectedSubscriptions}
                    onRemoveClick={(e, currentSub) => {
                      e.preventDefault();
                      setSelectedSubscriptions(
                        selectedSubscriptions.filter(
                          (s) => s.id !== currentSub.id,
                        ),
                      );
                    }}
                  />
                  {/* <IonButton onClick={() => setOpenTagModal(true)}>
                    <IonIcon icon={addCircleOutline}></IonIcon>
                  </IonButton> */}
                </IonItem>
                <IonItem onClick={() => setOpenTagModal(true)}>
                  <IonLabel className="custom-label">Tag</IonLabel>
                  <TagList
                    tags={selectedTags}
                    onRemoveClick={(e, currentTag) => {
                      e.preventDefault();
                      setSelectedTags(
                        selectedTags.filter((tag) => tag.id !== currentTag.id),
                      );
                    }}
                  />
                  {/* <IonButton onClick={() => setOpenTagModal(true)}>
                    <IonIcon icon={addCircleOutline}></IonIcon>
                  </IonButton> */}
                </IonItem>
                <IonItem onClick={() => setOpenAthletesModal(true)}>
                  <IonLabel className="custom-label">Allievi</IonLabel>
                  <ParticipantList
                    participants={selectedAthletes}
                    onRemoveClick={(e, currentParticipant) => {
                      e.preventDefault();
                      setSelectedAthletes(
                        selectedAthletes.filter(
                          (participant) =>
                            participant.userId !== currentParticipant.userId,
                        ),
                      );
                    }}
                  />
                </IonItem>
              </IonList>
            </form>
            <AthletesModal
              isOpen={openAthletesModal}
              athletes={athletes}
              selectedAthletes={selectedAthletes}
              onConfirm={(athletes) => {
                setSelectedAthletes(athletes);
                setOpenAthletesModal(false);
              }}
              onDismiss={() => setOpenAthletesModal(false)}
            />
            <SubscriptionsModal
              isOpen={openSubscriptionsModal}
              subscriptions={subscriptions}
              selectedSubscriptions={selectedSubscriptions}
              onConfirm={(subscriptions) => {
                setSelectedSubscriptions(subscriptions);
                setOpenSubscriptionsModal(false);
              }}
              onDismiss={() => setOpenSubscriptionsModal(false)}
            />
            <TagModal
              isOpen={openTagModal}
              tags={tags}
              selectedTags={selectedTags}
              onConfirm={(tags) => {
                setSelectedTags(tags);
                setOpenTagModal(false);
              }}
              onDismiss={() => setOpenTagModal(false)}
              onModalDismiss={() => {
                setOpenTagModal(false);
              }}
            />
          </>
        )}
        {!isLoading && parsedGroupId && (
          <IonButton routerLink={`/group-scheduler/${parsedGroupId}`}>
            Pianifica
          </IonButton>
          // <IonButton onClick={(e) => doPlanification()}>Calendarizza</IonButton>
        )}
      </IonContent>
      {!isLoading && (
        <IonFooter className="ion-no-border ion-padding">
          <IonButton
            color="success"
            expand="block"
            size="large"
            onClick={submitForm}
          >
            Salva
          </IonButton>
        </IonFooter>
      )}
    </IonPage>
  );
};

export default AddOrUpdateGroup;

AddOrUpdateGroup.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      groupId: PropTypes.string,
    }),
  }),
};
